import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useState
} from 'react';

import Tooltip from './components/tooltip/Tooltip';

import './AttachmentViewer.scss';
import Icon from '../Icon';
import Viewer from './Viewer';
import useTranslation from '../../hooks/useTranslation';
import Buttom from '../Button';

export function AttachmentViewer({
  mimetype, list = [], file, onClose,
}) {
  const { t, } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(-1);

  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex === list.length - 1 ? 0 : prevIndex + 1));
  }, [list.length]);
  const handlePrev = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? list.length - 1 : prevIndex - 1));
  }, [list.length]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'ArrowRight') {
        // Avanzar a la siguiente imagen
        handleNext();
      } else if (event.key === 'ArrowLeft') {
        // Retroceder a la imagen anterior
        handlePrev();
      }
    },
    [handleNext, handlePrev]
  );

  const currentFile = useMemo(() => {
    if (currentIndex !== -1) {
      return list[currentIndex] || list[currentIndex];
    }
    return file || file;
  }, [currentIndex, file, list]);

  const src = useMemo(() => {
    return currentFile.fileKeySigned || currentFile.fileKey;
  }, [currentFile.fileKey, currentFile.fileKeySigned]);

  const openFile = useCallback(() => {
    window.open(src, '_blank');
  }, [src]);

  const downloadFile = useCallback(async () => {
    const a = document.createElement('a');
    if (mimetype.startsWith('image/')) {
      window.open(src, '_blank');
      return;
    }
    a.href = src;
    a.download = currentFile.name;
    a.click();
  }, [src, currentFile.name, mimetype]);

  const getFormattedName = () => {
    const indexOfExtensionStarting = currentFile.name.lastIndexOf('.');
    const extension = currentFile.name.slice(indexOfExtensionStarting);
    const fileName = currentFile.name.slice(0, indexOfExtensionStarting);
    return (
      <p className="attachment-name">
        <span>{fileName}</span>
        {extension}
      </p>
    );
  };

  useEffect(() => {
    if (!list || !list.length) return;
    const index = list.findIndex((item) => item.key === file.key);
    if (index !== -1) {
      setCurrentIndex(index);
    } else {
      setCurrentIndex(-1);
    }
    // Añadir el listener del teclado
    window.addEventListener('keydown', handleKeyDown);
    // Limpiar el listener cuando el componente se desmonte
    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, list.length, file, list]);
  return (
    <div className="attachment-container">
      <div className="header">
        <div className="left-side">
          {list.length > 1 && <Icon icon="arrow-left" onClick={handlePrev} />}
          <div
            style={{
              marginLeft: 10,
              width: 'calc(100% - 50px)',
            }}
          >
            {getFormattedName()}
          </div>
          {list.length > 1 && <Icon icon="arrow-right" onClick={handleNext} />}
        </div>
        <div className="right-side">
          <div className="space" />
          <Tooltip title={t('Download')}>
            <Icon icon="fa-download" fontSize="24px" role="button" onClick={downloadFile} />
          </Tooltip>
          <div className="space" />
          <Tooltip title={t('Open in new tab')}>
            <Icon icon="fa-up-right-from-square" fontSize="24px" role="button" onClick={openFile} />
          </Tooltip>
          <div className="space" />
          <div className="separater" />
          <Tooltip title={t('Close')}>
            <Buttom id="close" onClick={onClose} theme="secundary">
              <Icon
                icon="fa-xmark-large"
                fontSize="24px"
                role="button"
                style={{ color: 'white', }}
              />
            </Buttom>
          </Tooltip>
        </div>
      </div>
      <div className="attachment-preview-container">
        <Viewer
          key={currentFile.id}
          file={currentFile}
          src={src}
          name={currentFile.name}
          mimetype={mimetype}
          list={list}
        />
      </div>
    </div>
  );
}

AttachmentViewer.propTypes = {
  mimetype: PropTypes.string.isRequired,
  list: PropTypes.array,
  file: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default AttachmentViewer;
